/**
 * Incremental MD5 → base64, the exact form GCS reports as `md5Hash`.
 *
 * Why hand-rolled: Web Crypto has no MD5, and the bulk-upload pre-flight
 * needs the same digest GCS computes so it can (a) skip files already in the
 * knowledgebase (registry `contentMd5`) and (b) let finalize verify the
 * upload end-to-end. MD5 is used here as a content fingerprint, not for
 * security.
 */

const S = [
  7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14,
  20, 5, 9, 14, 20, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 6, 10, 15, 21, 6,
  10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21,
];
const K = new Int32Array(64);
for (let i = 0; i < 64; i += 1) K[i] = Math.floor(Math.abs(Math.sin(i + 1)) * 2 ** 32) | 0;

export class Md5 {
  private a = 0x67452301 | 0;
  private b = 0xefcdab89 | 0;
  private c = 0x98badcfe | 0;
  private d = 0x10325476 | 0;
  private readonly block = new Uint8Array(64);
  private blockLen = 0;
  private totalBytes = 0;
  private readonly m = new Int32Array(16);

  update(data: Uint8Array): this {
    let offset = 0;
    this.totalBytes += data.length;
    if (this.blockLen > 0) {
      const take = Math.min(64 - this.blockLen, data.length);
      this.block.set(data.subarray(0, take), this.blockLen);
      this.blockLen += take;
      offset = take;
      if (this.blockLen === 64) {
        this.compress(this.block, 0);
        this.blockLen = 0;
      }
    }
    while (offset + 64 <= data.length) {
      this.compress(data, offset);
      offset += 64;
    }
    if (offset < data.length) {
      this.block.set(data.subarray(offset), 0);
      this.blockLen = data.length - offset;
    }
    return this;
  }

  digest(): Uint8Array {
    const bitLen = this.totalBytes * 8;
    const padLen = this.blockLen < 56 ? 56 - this.blockLen : 120 - this.blockLen;
    const pad = new Uint8Array(padLen + 8);
    pad[0] = 0x80;
    // 64-bit little-endian bit length (files are < 2^53 bytes).
    const lo = bitLen >>> 0;
    const hi = Math.floor(bitLen / 2 ** 32) >>> 0;
    for (let i = 0; i < 4; i += 1) {
      pad[padLen + i] = (lo >>> (8 * i)) & 0xff;
      pad[padLen + 4 + i] = (hi >>> (8 * i)) & 0xff;
    }
    const total = this.totalBytes;
    this.update(pad);
    this.totalBytes = total;
    const out = new Uint8Array(16);
    [this.a, this.b, this.c, this.d].forEach((word, w) => {
      for (let i = 0; i < 4; i += 1) out[w * 4 + i] = (word >>> (8 * i)) & 0xff;
    });
    return out;
  }

  private compress(buf: Uint8Array, off: number): void {
    const m = this.m;
    for (let i = 0; i < 16; i += 1) {
      const j = off + i * 4;
      m[i] = buf[j] | (buf[j + 1] << 8) | (buf[j + 2] << 16) | (buf[j + 3] << 24);
    }
    let a = this.a;
    let b = this.b;
    let c = this.c;
    let d = this.d;
    for (let i = 0; i < 64; i += 1) {
      let f: number;
      let g: number;
      if (i < 16) {
        f = (b & c) | (~b & d);
        g = i;
      } else if (i < 32) {
        f = (d & b) | (~d & c);
        g = (5 * i + 1) % 16;
      } else if (i < 48) {
        f = b ^ c ^ d;
        g = (3 * i + 5) % 16;
      } else {
        f = c ^ (b | ~d);
        g = (7 * i) % 16;
      }
      const tmp = d;
      d = c;
      c = b;
      const x = (a + f + K[i] + m[g]) | 0;
      b = (b + ((x << S[i]) | (x >>> (32 - S[i])))) | 0;
      a = tmp;
    }
    this.a = (this.a + a) | 0;
    this.b = (this.b + b) | 0;
    this.c = (this.c + c) | 0;
    this.d = (this.d + d) | 0;
  }
}

export function bytesToBase64(bytes: Uint8Array): string {
  let bin = '';
  for (const byte of bytes) bin += String.fromCharCode(byte);
  return btoa(bin);
}

export function md5Base64(data: Uint8Array): string {
  return bytesToBase64(new Md5().update(data).digest());
}

const READ_SLICE_BYTES = 4 * 1024 * 1024;

/** Stream a File/Blob through MD5 in 4 MB slices (bounded memory). */
export async function md5Base64OfBlob(blob: Blob): Promise<string> {
  const h = new Md5();
  for (let off = 0; off < blob.size; off += READ_SLICE_BYTES) {
    const buf = await blob.slice(off, off + READ_SLICE_BYTES).arrayBuffer();
    h.update(new Uint8Array(buf));
  }
  return bytesToBase64(h.digest());
}
